import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import { isEqualWith } from "lodash";
import { Select } from "components/select/index";

import { useTranslation } from "react-i18next";
import { GetTaxLine, PutArchiveTaxLine, PutTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import axios, { CancelToken } from "axios";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { TAccountingReference } from "redux/reducers/models/facility";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { GetProvince } from "api/rpc/country";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { Badge } from "components/badge/Badge";
import Toggle from "components/form/toggle/Toggle";
import { accountingReferenceMatchesQuery, formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import ArchiveTaxLine from "components/archivePopup/archivePopup";

interface ITaxLineState {
  id: number;
  name: string;
  percentage: number;
  migrate_tax_line_id?: number;
  accountingReferences: Array<TAccountingReference>;
  accounting_reference_id: number;
  accountingRefSearch: string;
  provinceName: string;
  default: boolean;
  compound: boolean;
  tax_exempt: boolean;
}

export default function TaxLinesEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { facilityStore, authStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const [taxLineState, setTaxLineState] = useState<ITaxLineState>({
    id: undefined,
    name: "",
    percentage: null,
    migrate_tax_line_id: undefined,
    accountingReferences: [],
    accounting_reference_id: null,
    accountingRefSearch: "",
    provinceName: "",
    default: false,
    compound: false,
    tax_exempt: false,
  });

  const [archiveWarningState, setArchiveWarningState] = useState<boolean>(false);
  const [prevTaxLineState, setPrevTaxLineState] = useState<ITaxLineState>(taxLineState);
  const [taxLineBeforeChanges, setTaxLineBeforeChanges] = useState<ITaxLineState>(undefined);
  const [taxLineLoaded, setTaxLineLoaded] = useState<boolean>(false);
  const [accountingReferencesLoaded, setAccountingReferencesLoaded] = useState<boolean>(false);

  useEffect(() => {
    void loadTaxLines();

    return () => {
      setTaxLineState({
        id: undefined,
        name: "",
        percentage: null,
        migrate_tax_line_id: undefined,
        accountingReferences: [],
        accounting_reference_id: null,
        accountingRefSearch: "",
        provinceName: "",
        default: false,
        compound: false,
        tax_exempt: false,
      });
      setPrevTaxLineState({
        id: undefined,
        name: "",
        percentage: null,
        migrate_tax_line_id: undefined,
        accountingReferences: [],
        accounting_reference_id: null,
        accountingRefSearch: "",
        provinceName: "",
        default: false,
        compound: false,
        tax_exempt: false,
      });
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccountingReferences(source.token);
  }, []);

  async function loadTaxLines() {
    const [res, provinceRes] = await Promise.all([
      GetTaxLine(null, true),
      GetProvince({ country_id: facilityStore?.facility?.country_id }, true),
    ]);
    if (res.status !== StatusCode.OK) {
      return;
    }

    if (provinceRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting provinces")));
      return;
    }

    const currentType = res.data.find((type: any) => type.id === Number(id));
    const foundProvince = provinceRes?.data?.find(province => province?.id === currentType?.province_id);
    setTaxLineState(prev => ({
      ...prev,
      id: currentType.id,
      name: currentType.name,
      percentage: currentType.percentage,
      accounting_reference_id: currentType.accounting_reference_id,
      provinceName: foundProvince?.name,
      default: !!currentType?.default,
      compound: !!currentType?.compound,
      tax_exempt: !!currentType?.tax_exempt,
    }));

    setPrevTaxLineState(prev => ({
      ...prev,
      id: currentType.id,
      name: currentType.name,
      percentage: currentType.percentage,
      accounting_reference_id: currentType.accounting_reference_id,
      provinceName: foundProvince?.name,
      default: !!currentType?.default,
      compound: !!currentType?.compound,
      tax_exempt: !!currentType?.tax_exempt,
    }));

    setTaxLineLoaded(true);
  }

  async function loadAccountingReferences(token: CancelToken) {
    const res = await GetAccountingReference(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    const normalizedReferences: TAccountingReference[] = [
      { id: -1, client_id: null as number, title: t("None"), reference_number: null as string },
      ...res.data,
    ];

    setTaxLineState(prev => ({ ...prev, accountingReferences: normalizedReferences }));
    setAccountingReferencesLoaded(true);
  }

  async function saveTaxLine() {
    const res = await PutTaxLine(taxLineState, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/taxes");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setTaxLineState(prevState => ({ ...prevState, [id]: id === "percentage" ? Number(value) : value }));
  }

  function cancelUnsavedChanges() {
    setTaxLineState(taxLineBeforeChanges);
  }

  function unsavedChangesExist() {
    if (taxLineBeforeChanges === undefined) {
      if (taxLineLoaded && accountingReferencesLoaded) {
        setTaxLineBeforeChanges(taxLineState);
      }
      return false;
    }

    return !isEqualWith(taxLineBeforeChanges, taxLineState);
  }

  async function handleArchiveTaxLine() {
    const archiveTaxLineRes = await PutArchiveTaxLine({ tax_line_id: Number(id) }, true);

    if (archiveTaxLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error archiving tax line")));
      return;
    }

    dispatch(showSuccess(t("Tax line successfully archived")));
    setArchiveWarningState(false);
    history.goBack();
  }

  return (
    <>
      <Page
        title={t("Edit Tax Line: ") + prevTaxLineState.name}
        narrow
        titleMetadata={
          taxLineState?.provinceName && (
            <Badge size="medium" type="primary">
              {taxLineState?.provinceName}
            </Badge>
          )
        }
        breadcrumbs={[
          {
            prefix: true,
            label: t("Tax Lines"),
            url: "/admin/settings/taxes",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveTaxLine,
          onCancel: cancelUnsavedChanges,
        }}
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Archive"),
                icon: "archive",

                handler: () => setArchiveWarningState(true),
                disabled: !authStore?.user?.permissions?.tax_lines_archive,
              },
            ],
          },
        }}
      >
        <Card>
          <Card.Section>
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={taxLineState.name}
                    label={t("Name")}
                    id="name"
                    onChange={handleInputChange}
                    placeholder={t("Name")}
                  />
                  <Input
                    value={taxLineState.percentage}
                    label={t("Percentage")}
                    id="percentage"
                    onChange={handleInputChange}
                    placeholder={t("percentage")}
                    type={"number"}
                    suffix="%"
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  {taxLineState?.accounting_reference_id ? (
                    <div>
                      <div>
                        <p className="inventory-vendor-label">{t("Accounting Reference")}</p>
                      </div>
                      <div className="vendor-name">
                        <div>
                          {" "}
                          {(() => {
                            const ref = taxLineState?.accountingReferences?.find(
                              (r: TAccountingReference) => r.id === Number(taxLineState?.accounting_reference_id),
                            );
                            return ref ? formatAccountingReferenceLabel(ref.title ?? "", ref.reference_number) : "";
                          })()}{" "}
                        </div>
                        <button
                          onClick={() =>
                            setTaxLineState(prevState => ({
                              ...prevState,
                              accounting_reference_id: null,
                              accountingRefSearch: "",
                            }))
                          }
                        >
                          <Icon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Select
                      label={t("Accounting Reference")}
                      showSearch
                      onSearch={(query: string) => setTaxLineState(prev => ({ ...prev, accountingRefSearch: query }))}
                      onChange={(value: number) =>
                        setTaxLineState(prev => ({ ...prev, accounting_reference_id: value }))
                      }
                      allowClear
                      placeholder={t("Search for Accounting References...")}
                      searchValue={taxLineState.accountingRefSearch}
                      showDropDownOnFocus={true}
                      defaultValue={taxLineState.accounting_reference_id}
                    >
                      {taxLineState.accountingReferences
                        .filter(accountRef =>
                          accountingReferenceMatchesQuery(
                            accountRef.title ?? "",
                            accountRef.reference_number,
                            taxLineState.accountingRefSearch,
                          ),
                        )
                        .map(reference => {
                          const label = formatAccountingReferenceLabel(
                            reference.title ?? "",
                            reference.reference_number,
                          );
                          return (
                            <div key={reference.id}>
                              {taxLineState.accountingRefSearch !== "" ? (
                                <div>
                                  <Select.Option key={reference.id} value={reference.id} name={label}>
                                    <p className="font-semibold text-lg">{label}</p>
                                  </Select.Option>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                    </Select>
                  )}
                </FormLayout.Group>
                <FormLayout.Group>
                  <Toggle
                    label={t("Default")}
                    checked={taxLineState?.default}
                    id="default"
                    onChange={e => setTaxLineState(prev => ({ ...prev, [e.target.id]: e.target.checked }))}
                    labelSupport={t("Enable to automatically apply this tax to new products")}
                  />
                  <Toggle
                    label={t("Compound")}
                    checked={taxLineState?.compound}
                    id="compound"
                    onChange={e => setTaxLineState(prev => ({ ...prev, [e.target.id]: e.target.checked }))}
                    labelSupport={t("Compound tax lines are added to the total amount of the order")}
                  />
                  <Toggle
                    label={t("Tax Exempt")}
                    checked={taxLineState.tax_exempt}
                    id="tax_exempt"
                    onChange={event => setTaxLineState(prev => ({ ...prev, [event.target.id]: event.target.checked }))}
                    labelSupport={t("Enable to allow this tax line to be exempted on eligible transactions")}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Card.Section>
        </Card>

        <ArchiveTaxLine
          isOpen={archiveWarningState}
          title={t("Archive tax line?")}
          onCancel={() => setArchiveWarningState(false)}
          onOk={handleArchiveTaxLine}
        />
      </Page>
    </>
  );
}
